import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDownTrayIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { List, Row, PaginatedResponse, DatasetDetail } from '../types'
import toast from 'react-hot-toast'
import DataTable from '../components/DataTable'
import ExportModal from '../components/ExportModal'

export default function ListDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [page, setPage] = useState(1)
  const [showExport, setShowExport] = useState(false)
  
  // Fetch list details
  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['list', id],
    queryFn: async () => {
      const res = await api.get<List>(`/lists/${id}`)
      return res.data
    },
    enabled: !!id,
  })
  
  // Fetch parent dataset for column info
  const { data: dataset } = useQuery({
    queryKey: ['dataset', list?.dataset_id],
    queryFn: async () => {
      const res = await api.get<DatasetDetail>(`/datasets/${list!.dataset_id}`)
      return res.data
    },
    enabled: !!list?.dataset_id,
  })
  
  // Fetch list rows
  const { data: rowsData, isLoading: rowsLoading } = useQuery({
    queryKey: ['list-rows', id, page],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Row>>(
        `/lists/${id}/rows`,
        { params: { page, page_size: 50 } }
      )
      return res.data
    },
    enabled: !!id && !!list,
  })
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/lists/${id}`)
    },
    onSuccess: () => {
      toast.success('List deleted')
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      navigate('/lists')
    },
    onError: () => {
      toast.error('Failed to delete list')
    },
  })
  
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this list?')) {
      deleteMutation.mutate()
    }
  }
  
  if (listLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  if (!list) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">List not found</p>
      </div>
    )
  }
  
  const columns = dataset?.columns.map(col => col.name) || []
  
  return (
    <div className="h-full flex flex-col">
      {/* Back link */}
      <Link to="/lists" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 flex-shrink-0">
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Lists
      </Link>
      
      {/* Header */}
      <div className="flex justify-between items-start mb-6 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{list.name}</h1>
          {list.description && (
            <p className="mt-1 text-sm text-gray-600">{list.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              list.list_type === 'static'
                ? 'bg-gray-100 text-gray-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {list.list_type}
            </span>
            <span>•</span>
            <span>{(list.cached_count || rowsData?.total || 0).toLocaleString()} rows</span>
            <span>•</span>
            <Link 
              to={`/datasets/${list.dataset_id}`}
              className="text-primary-600 hover:text-primary-700"
            >
              View Dataset
            </Link>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowExport(true)}
            className="btn-primary"
            disabled={!dataset}
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export
          </button>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Dynamic list filter info */}
      {list.list_type === 'dynamic' && list.filter_definition && (
        <div className="card p-4 mb-6 bg-blue-50 border-blue-200 flex-shrink-0">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Dynamic Filter</h3>
          <p className="text-sm text-blue-700">
            This list automatically includes rows matching the saved filter criteria.
          </p>
        </div>
      )}
      
      {/* Data table */}
      <div className="card flex-1 flex flex-col min-h-0">
        {rowsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : rowsData?.items.length ? (
          <>
            <div className="flex-1 min-h-0">
              <DataTable
                columns={columns}
                rows={rowsData.items}
                maxHeight="100%"
              />
            </div>
            {/* Pagination */}
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
              <div className="text-sm text-gray-700">
                Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, rowsData.total)} of{' '}
                {rowsData.total.toLocaleString()} rows
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= rowsData.pages}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No rows in this list</p>
            {list.list_type === 'static' && (
              <p className="text-sm text-gray-400 mt-2">
                Add rows from the dataset view
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Export modal */}
      {showExport && dataset && (
        <ExportModal
          dataset={dataset}
          filter={list.list_type === 'dynamic' ? list.filter_definition || null : null}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  )
}
